import React, { useCallback, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaBars, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { toast } from "react-toastify";
import Dropzone from "react-dropzone";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

import HeaderContainer from "../CrewDashboard/HeaderContainer";
import { supabase } from "../../supabaseClient";
import { ErrorList } from "../common/error";
import {
  endsWithTime,
  getAirlineFromEntry,
  getDropoffTimeFromEntry,
  getFlightNoFromEntry,
  passValidation,
} from "../Utils/import";

import DriverNewNote from "./DriverNewNote";
import HeadQuarters from "./HeadQuarters";
import CompaniesNew from "./CompaniesNew";
import HQCompanyNew from "./HQCompanyNew";
import DriverCredentialTypeNew from "./DriverCredentialTypeNew";
import DriverInfractionTypesNew from "./DriverInfractionTypesNew";
import LocationTypesNew from "./LocationTypesNew";

import LocationNew from "./LocationNew";
import AirportNew from "./AirportNew";
import AirlineNew from "./AirlineNew";
import HotelNew from "./HotelNew";
import ScheduleNoteTypesNew from "./ScheduleNoteTypesNew";
import ScheduleStatusesTypesNew from "./ScheduleStatusesTypesNew";
import ScheduleTransportationTypesNew from "./ScheduleTransportationTypesNew";
import ScheduleTypesNew from "./ScheduleTypesNew";
import VehicleLogTypesNew from "./VehicleLogTypesNew";
import VehicleStatusesTypesNew from "./VehicleStatusesTypesNew";
import ClientListNew from "./ClientListNew";
import DriverListNew from "./DriverListNew";
import DriverCredentialsNew from "./DriverCredentialsNew";
import DriverInfractionsNew from "./DriverInfractionsNew";
import DriverMedicalNew from "./DriverMedicalNew";
import RatesNew from "./RatesNew";
import ScheduleListNew from "./ScheduleListNew";
import ScheduleNotesNew from "./ScheduleNotesNew";
import VehicleListNew from "./VehicleListNew";
import VehicleLogsNew from "./VehicleLogsNew";

const SuperAdminDetails = () => {
  const navigate = useNavigate(); // Use useNavigate instead of useHistory

  const [fileName, setFileName] = useState();
  const [jsonList, setJsonList] = useState([]);

  const [isProcessingCSV, setProcessingCSV] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setProcessingCSV(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const worksheetName = workbook.SheetNames[1] ?? workbook.SheetNames[0];
        if (!worksheetName) return;
        const xlsData = XLSX.utils.sheet_to_json(
          workbook.Sheets[worksheetName],
          {
            header: 18,
          }
        );
        let startIndex = 0;
        let dateKey,
          departmentKey,
          pickupKey,
          dropoffKey,
          airportKey,
          passengerKey,
          pickupTimeKey;
        for (let i = 0; i < xlsData.length; i++) {
          const _row = xlsData[i];
          const _rowData = Object.values(_row);
          const _rowKeys = Object.keys(_row);
          if (
            _rowData.includes("DATE") &&
            _rowData.includes("DEPARTMENT") &&
            _rowData.includes("#RIDERS")
          ) {
            startIndex = i + 1;

            departmentKey = _rowKeys.find((key) => _row[key] == "DEPARTMENT");
            dateKey = _rowKeys.find((key) => _row[key] == "DATE");
            passengerKey = _rowKeys.find((key) => _row[key] == "#RIDERS");
          } else if (
            _rowData.includes("Location") &&
            _rowData.includes("From") &&
            _rowData.includes("Pickup Date/Time") &&
            _rowData.includes("To")
          ) {
            airportKey = _rowKeys.find((key) => _row[key] == "Location");
            pickupTimeKey = _rowKeys.find(
              (key) => _row[key] == "Pickup Date/Time"
            );

            pickupKey = _rowKeys.find((key) => _row[key] == "From");
            dropoffKey = _rowKeys.find((key) => _row[key] == "To");
            break;
          }
        }
        if (
          !dateKey ||
          !departmentKey ||
          !pickupKey ||
          !dropoffKey ||
          !airportKey ||
          !passengerKey ||
          !pickupTimeKey
        ) {
          console.error("Excel format is not supported");
          toast.error("Error while reading xls file!!!");
          return;
        }

        const rawJsonData = xlsData.filter(
          (_item, index) => index > 8 && !!_item[departmentKey]
        );
        const jsonData = [];
        const errorData = [];

        let curDate = rawJsonData[0][dateKey];

        for (const _item of rawJsonData) {
          if (_item[dateKey]) curDate = _item[dateKey];
          const scheduleDate = dayjs(`${curDate}`, "DD-MMM-YYYY");
          const passenger = _item[passengerKey];
          const airport = _item[airportKey];
          const pickup = _item[pickupKey];
          const dropoff = _item[dropoffKey];
          const newItem = {
            scheduleDate,
            passenger,
            airport,
            pickup,
            dropoff,
          };
          if (!passValidation(newItem)) errorData.push(Object.values(_item));
          let transformed = {
            company: "ACT",
            status: "ACTIVE",
            pilots: 0,
            flightCrew: 0,
            code: "",
            rate: 0,
            airport: newItem.airport,
            passenger: newItem.passenger,
          };
          if (endsWithTime(newItem.pickup)) {
            const pickupSplit = newItem.pickup.split("-");
            const dropoffHotel = newItem.dropoff;
            const airline = getAirlineFromEntry(pickupSplit);
            const flightNo = getFlightNoFromEntry(pickupSplit);
            const time = getDropoffTimeFromEntry(
              pickupSplit,
              scheduleDate.year()
            );
            transformed.dropoffLocation = dropoffHotel;
            transformed.pickupLocation = newItem.airport;
            transformed.flightNo = flightNo;
            transformed.scheduleDate = time;
            transformed.pass = newItem.passenger;
            transformed.client = airline;
            transformed.type = "ARRIVE";
          } else if (endsWithTime(newItem.dropoff)) {
            const dropoffSplit = newItem.dropoff.split("-");
            const pickupHotel = newItem.pickup;
            const dropoffAirline = getAirlineFromEntry(dropoffSplit);
            const dropoffFlightNo = getFlightNoFromEntry(dropoffSplit);
            const dropoffTime = getDropoffTimeFromEntry(
              dropoffSplit,
              scheduleDate.year()
            );
            transformed.dropoffLocation = newItem.airport;
            transformed.pickupLocation = pickupHotel;
            transformed.flightNo = dropoffFlightNo;
            transformed.scheduleDate = dropoffTime || scheduleDate.toDate();
            transformed.pass = newItem.passenger;
            transformed.client = dropoffAirline;
            transformed.type = "DEPART";
          } else {
            throw new Error("Invalid pickup/dropoff location format");
          }
          // Set driver and vehicle based on airport
          const clientUpper = transformed.client.toUpperCase();
          switch (newItem.airport.toUpperCase()) {
            case "LAX":
              transformed.driver = "0152";
              transformed.vehicle = "1002";
              if (clientUpper === "B6") transformed.rate = 62;
              if (clientUpper === "QX") transformed.rate = 65;
              if (clientUpper === "AS") transformed.rate = 65;
              break;
            case "BUR":
              transformed.driver = "0152";
              transformed.vehicle = "1002";
              if (clientUpper === "B6") transformed.rate = 44;
              break;
            case "SDF":
              transformed.driver = "0203";
              transformed.vehicle = "2200";
              if (clientUpper === "WN") transformed.rate = 73;
              break;
            case "LAS":
              transformed.driver = "0178";
              transformed.vehicle = "1701";
              if (clientUpper === "B6") transformed.rate = 39.43;
              if (clientUpper === "RV") transformed.rate = 38;
              if (clientUpper === "AM") transformed.rate = 54;
              if (clientUpper === "AC") transformed.rate = 38;
              break;
            case "MCO":
              transformed.driver = "0193";
              transformed.vehicle = "1500";
              if (clientUpper === "B6") transformed.rate = 40.8;
              if (clientUpper === "WN") transformed.rate = 60;
              break;
            case "MSP":
              transformed.driver = "0201";
              transformed.vehicle = "2001";
              if (clientUpper === "WN") transformed.rate = 70;
              break;
            case "CVG":
              transformed.driver = "0202";
              transformed.vehicle = "2100";
              if (clientUpper === "WN") transformed.rate = 95;
              break;
            case "PHL":
              transformed.driver = "0163";
              transformed.vehicle = "1201";
              if (clientUpper === "F9") transformed.rate = 48;
              break;
            case "DAY":
              transformed.driver = "0168";
              transformed.vehicle = "601";
              if (clientUpper === "ENDEAVOR") transformed.rate = 100;
              if (clientUpper === "9E") transformed.rate = 70;
              break;
            case "BUF":
              transformed.Driver = "0204";
              transformed.Vehicle = "2300";
              transformed.Rate = 70;
              break;
            default:
              throw new Error(
                `Airport ${newItem.airport} is not currently supported`
              );
          }

          jsonData.push(transformed);
        }
        setFileName(acceptedFiles[0].name);
        setJsonList(jsonData);
        setErrorList(errorData);
      } catch (_err) {
        console.error("Error reading xls file", _err);
        toast.error("Error while reading xls file!!!");
      }
      setProcessingCSV(false);
    };
    reader.onerror = () => {
      setProcessingCSV(false);
    };

    reader.readAsArrayBuffer(acceptedFiles[0]);
  }, []);

  const onImportSuccess = useCallback(() => {
    navigate("/super-admin#schedule-table");
  }, [navigate]);
  const onImport = useCallback(async () => {
    setIsImporting(true);
    const now = dayjs();

    const airlineLetterCodeList = [];
    const companyNameList = [];
    const driverCodeList = [];
    const vehicleCodeList = [];
    const transportCodeList = [];
    const statusCodeList = [];
    const pickupLocationCodeList = [];
    const dropoffLocationCodeList = [];

    try {
      for (const item of jsonList) {
        // Transform data (same logic as your frontend)

        // Get foreign keys
        if (!airlineLetterCodeList.includes(item.client))
          airlineLetterCodeList.push(item.client);
        if (!companyNameList.includes(item.company))
          companyNameList.push(item.company);
        if (!driverCodeList.includes(item.driver))
          driverCodeList.push(item.driver);
        if (!vehicleCodeList.includes(item.vehicle))
          vehicleCodeList.push(item.vehicle);
        if (!transportCodeList.includes(item.type))
          transportCodeList.push(item.type);
        if (!statusCodeList.includes(item.status))
          statusCodeList.push(item.status);
        if (!pickupLocationCodeList.includes(item.pickupLocation))
          pickupLocationCodeList.push(item.pickupLocation);
        if (!dropoffLocationCodeList.includes(item.dropoffLocation))
          dropoffLocationCodeList.push(item.dropoffLocation);
      }
    } catch (_err) {
      console.log("Error while converting xls data");
      toast.error(`Import failed. Failed at converting xls data.`);
    }

    const { data, error } = await supabase.functions.invoke(
      "import-schedules",
      {
        body: {
          transformedList: jsonList,
          airlineLetterCodeList,
          companyNameList,
          driverCodeList,
          vehicleCodeList,
          transportCodeList,
          statusCodeList,
          pickupLocationCodeList,
          dropoffLocationCodeList,
        },
      }
    );
    console.log(data, error);

    setIsImporting(false);
    const endTime = dayjs();
    const diffTime = endTime.diff(now, "second");
    console.log("difftime", diffTime);

    if (error || data == null) toast.error("Error while importing schedules.");
    const errorCount = data.errorCount;
    const importCount = data.importedCount;
    if (errorCount == 0 && importCount > 0) {
      toast.success("Imported all schedules successfully!");
      onImportSuccess();
    } else if (errorCount > 0 && importCount > 0) {
      toast.info(
        `Imported ${errorCount} schedules while getting errors on ${errorCount} schedules.`
      );
      setErrorList(data.errors);
    } else if (errorCount > 0) {
      toast.error(
        `Import failed. Failed at importing ${errorCount} schedules.`
      );
      setErrorList(data.errors);
    } else if (data.isDuplicated) {
      toast.info(
        `Seems like the xls file is already imported. Pls try different xls file.`
      );
    }
  }, [jsonList, onImportSuccess]);

  const [pageStatus, setPageStatus] = useState("HQList");
  // const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const driversPerPage = 15; // 3 rows of 5 cards each

  const drivers = Array.from({ length: 20 }, (_, i) => ({
    id: i + 1,
    name: `Driver ${i + 1}`,
    experience: `${
      Math.floor(Math.random() * 10) + 1
    } Years Driving Experience`,
  }));
  const totalPages = Math.ceil(drivers.length / driversPerPage);

  // Pagination Logic
  const indexOfLastDriver = currentPage * driversPerPage;
  const indexOfFirstDriver = indexOfLastDriver - driversPerPage;
  const currentDrivers = drivers.slice(indexOfFirstDriver, indexOfLastDriver);
  const addNew = () => {
    // navigate("/manager-edit");
  };

  const handleSave = () => {
    navigate("/manager-edit");
  };

  const handleBack = () => {
    sessionStorage.getItem("UserRole") === "1"
      ? navigate("/super-admin")
      : navigate("/manager");
  };
  const handleHQ = (title) => {
    setPageStatus(title);
    // Close mobile menu when menu item is clicked
    setIsMobileMenuOpen(false);
  };
  const sidebarClass = (key) =>
    `flex justify-between mb-4 cursor-pointer hover-row${pageStatus === key ? ' sidebar-active' : ''}`;
  // const onScheduleImport = useCallback((_option) => {
  //   navigate("/import-xls");
  // }, []);
  // const handleSort = (key) => {
  //     let direction = "asc";
  //     if (sortConfig.key === key && sortConfig.direction === "asc") {
  //         direction = "desc";
  //     }
  //     setSortConfig({ key, direction });
  // };
  const HQContent = ({ title }) => {
    return (
      <div>
        <div className="flex ">
          <h5 className="mb-0">{title}</h5>
          <button
            className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
            onClick={addNew}
          >
            <span>Add New</span>
          </button>
        </div>
        <div className="flex mt-4 items-center condition-bar">
          <div>
            <p className="mb-0">Look For</p>
            <div className="flex justify-between w-10 ml-1">
              <select className="custom-input rounded-lg cursor-pointer">
                <option>Hqid</option>
              </select>
            </div>
          </div>

          <div className="ml-1">
            <p className="mb-0">Look For</p>
            <div className="flex justify-between w-10 ml-1">
              <select className="custom-input rounded-lg cursor-pointer">
                <option>Hqid</option>
              </select>
            </div>
          </div>
          <div className="ml-1">
            <input type="" className="custom-input rounded-lg w-100 " />
          </div>
          <div className="ml-1 flex">
            <div>
              <button className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none">
                <span>Search</span>
              </button>
            </div>
            <div>
              <button className="justify-between rounded-lg ml-1 flex items-center custom-reset-button">
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>
        <table className="table-auto w-full mt-4 border border-gray-200">
          <thead>
            <tr className="bg-gray-100 cursor-pointer">
              <th className="px-4 py-2 border">ID</th>
              <th className="px-4 py-2 border">Name</th>
              <th className="px-4 py-2 border">Experience</th>
            </tr>
          </thead>
          <tbody>
            {currentDrivers.map((driver) => (
              <tr key={driver.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 border">{driver.id}</td>
                <td className="px-4 py-2 border">{driver.name}</td>
                <td className="px-4 py-2 border">{driver.experience}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination-container justify-start">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            <FaArrowLeft className="pagination-icon" />
          </button>

          {Array.from(
            { length: Math.ceil(drivers.length / driversPerPage) },
            (_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`pagination-button ${
                  currentPage === i + 1 ? "active" : ""
                }`}
              >
                {i + 1}
              </button>
            )
          )}

          <button
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(prev + 1, Math.ceil(drivers.length / driversPerPage))
              )
            }
            disabled={indexOfLastDriver >= drivers.length}
            className="pagination-button"
          >
            <FaArrowRight className="pagination-icon" />
          </button>
        </div>
      </div>
    );
  };

  const ScheduleContent = ({ title }) => {
    return (
      <div>
        <div className="flex ">
          <h5 className="mb-0">{title}</h5>
        </div>

        <div className="items-center mt-5 personal-info-content">
          <div className="w-100 mt-4 rounded-lg">
            <div className="flex mb-2">
              <div className="flex-1 flex-center">
                <Dropzone onDrop={onDrop}>
                  {({ getRootProps, getInputProps }) => (
                    <section className="dropzone">
                      <div
                        {...getRootProps()}
                        className="w-full h-full flex-center"
                      >
                        <input
                          {...getInputProps()}
                          accept={[
                            "application/vnd.ms-excel",
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                          ]}
                        />
                        <p>Click to upload or drag and drop</p>
                        <div>File (xlsx)</div>
                      </div>
                    </section>
                  )}
                </Dropzone>
                {fileName ? (
                  <div className="file-name-label">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M10.0003 18.3337C14.5837 18.3337 18.3337 14.5837 18.3337 10.0003C18.3337 5.41699 14.5837 1.66699 10.0003 1.66699C5.41699 1.66699 1.66699 5.41699 1.66699 10.0003C1.66699 14.5837 5.41699 18.3337 10.0003 18.3337Z"
                        stroke="#00AF06"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.45801 9.99993L8.81634 12.3583L13.5413 7.6416"
                        stroke="#00AF06"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{fileName}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="mt-4 import-buttons">
            <button
              onClick={onImport}
              disabled={isImporting || isProcessingCSV}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button border-none"
            >
              {isImporting ? (
                <span className="loader"></span>
              ) : (
                <span>Import</span>
              )}
            </button>
            <div>
              {/* <select className="custom-input rounded-lg"> 
                                            <option>BUR</option>
                                        </select> */}
              <div className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button">
                <span>Cancel</span>
              </div>
            </div>
          </div>
          <ErrorList list={errorList} />
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      {/* Top Section */}
      <div className="items-center mt-5">
        <div
          onClick={handleBack}
          className="flex cursor-pointer justify-between items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        <div className="pt-4 w-full flex justify-between items-center">
          <p className="font-semibold text-lg">SAFE SUPER ADMIN</p>
          {/* Mobile Hamburger Menu */}
          {isMobileMenuOpen || (
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md-hidden p-2 rounded-lg bg-blue-500 border-none hover:bg-blue-600 transition-colors"
            >
              {isMobileMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
          )}
        </div>

        <div className="w-100 mt-4 flex justify-between rounded-lg">
          <div className="w-40 rounded-lg admin-left-navbar hidden md:block">
            <div className="left-navbar-header rounded-lg">
              <span>Admin</span>
            </div>
            <div className="p-10">
              <div onClick={() => handleHQ("HQList")} className={sidebarClass("HQList")}>
                <div className="hover-text"><span>Headquarters</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("CompaniesList")} className={sidebarClass("CompaniesList")}>
                <div className="hover-text"><span>Companies</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("HQCompanies")} className={sidebarClass("HQCompanies")}>
                <div className="hover-text"><span>HQ Companies</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("DriverCredentialTypes")} className={sidebarClass("DriverCredentialTypes")}>
                <div className="hover-text"><span>Driver Credential Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("DriverInfractionTypes")} className={sidebarClass("DriverInfractionTypes")}>
                <div className="hover-text"><span>Driver Infraction Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("LocationTypes")} className={sidebarClass("LocationTypes")}>
                <div className="hover-text"><span>Location Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("ScheduleNoteTypes")} className={sidebarClass("ScheduleNoteTypes")}>
                <div className="hover-text"><span>Schedule Note Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("ScheduleStatusesTypes")} className={sidebarClass("ScheduleStatusesTypes")}>
                <div className="hover-text"><span>Schedule Statuses Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("ScheduleTransportationTypes")} className={sidebarClass("ScheduleTransportationTypes")}>
                <div className="hover-text"><span>Schedule Transportation Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("ScheduleTypes")} className={sidebarClass("ScheduleTypes")}>
                <div className="hover-text"><span>Schedule Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("VehicleLogTypes")} className={sidebarClass("VehicleLogTypes")}>
                <div className="hover-text"><span>Vehicle Log Types</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("VehicleStatusesTypes")} className={sidebarClass("VehicleStatusesTypes")}>
                <div className="hover-text"><span>Vehicle Statuses</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("AirlineList")} className={sidebarClass("AirlineList")}>
                <div className="hover-text"><span>Airlines</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
            </div>
            <div className="left-navbar-subheader">
              <span>Client</span>
            </div>
            <div className="p-10">
              <div onClick={() => handleHQ("ClientList")} className={sidebarClass("ClientList")}>
                <div className="hover-text"><span>Client List</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
            </div>
            <div className="left-navbar-subheader">
              <span>Drivers</span>
            </div>
            <div className="p-10">
              <div onClick={() => handleHQ("DriverList")} className={sidebarClass("DriverList")}>
                <div className="hover-text"><span>Driver Lists</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("DriverCredentials")} className={sidebarClass("DriverCredentials")}>
                <div className="hover-text"><span>Driver Credentials</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("DriverInfractions")} className={sidebarClass("DriverInfractions")}>
                <div className="hover-text"><span>Driver Infractions</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("DriverMedical")} className={sidebarClass("DriverMedical")}>
                <div className="hover-text"><span>Driver Medical</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("DriverNotes")} className={sidebarClass("DriverNotes")}>
                <div className="hover-text"><span>Driver Notes</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
            </div>
            <div className="left-navbar-subheader">
              <span>Locations</span>
            </div>
            <div className="p-10">
              <div onClick={() => handleHQ("LocationList")} className={sidebarClass("LocationList")}>
                <div className="hover-text"><span>Location List</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("AirportList")} className={sidebarClass("AirportList")}>
                <div className="hover-text"><span>Airports</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("HotelList")} className={sidebarClass("HotelList")}>
                <div className="hover-text"><span>Hotels</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
            </div>
            <div className="left-navbar-subheader">
              <span>Schedule</span>
            </div>
            <div className="p-10">
              <div onClick={() => handleHQ("ScheduleList")} className={sidebarClass("ScheduleList")}>
                <div className="hover-text"><span>Schedule Lists</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("ScheduleNotes")} className={sidebarClass("ScheduleNotes")}>
                <div className="hover-text"><span>Schedule Notes</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("ScheduleImport")} className={sidebarClass("ScheduleImport")}>
                <div className="hover-text"><span>Schedule Import</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("RatesList")} className={sidebarClass("RatesList")}>
                <div className="hover-text"><span>Default Rates</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
            </div>
            <div className="left-navbar-subheader">
              <span>Vehicles</span>
            </div>
            <div className="p-10">
              <div onClick={() => handleHQ("VehicleLists")} className={sidebarClass("VehicleLists")}>
                <div className="hover-text"><span>Vehicle Lists</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
              <div onClick={() => handleHQ("VehicleLogs")} className={sidebarClass("VehicleLogs")}>
                <div className="hover-text"><span>Vehicle Logs</span></div>
                <div className="hover-text"><FaArrowRight /></div>
              </div>
            </div>
          </div>

          {/* Mobile Sidebar */}
          {isMobileMenuOpen && (
            <div className="md-hidden fixed inset-0 z-50">
              {/* Overlay */}
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={() => setIsMobileMenuOpen(false)}
              />
              {/* Mobile Sidebar */}
              <div className="absolute top-0 left-0 w-64 h-full bg-white shadow-lg">
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-lg">Menu</span>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 rounded-lg bg-red-500 border-none hover:bg-red-600 transition-colors"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="left-navbar-header rounded-lg mb-4">
                    <span>Admin</span>
                  </div>
                  <div
                    onClick={() => handleHQ("HQList")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Headquarters</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("CompaniesList")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Companies</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("HQCompanies")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>HQ Companies</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("DriverCredentialTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Driver Credential Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("DriverInfractionTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Driver Infraction Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("LocationTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Location Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("ScheduleStatusesTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Schedule Statuses Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("ScheduleTransportationTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Schedule Transportation Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("ScheduleTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Schedule Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("ScheduleNoteTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Schedule Note Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("VehicleStatusesTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Vehicle Statuses Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                  <div
                    onClick={() => handleHQ("VehicleLogTypes")}
                    className="flex justify-between mb-4 cursor-pointer hover-row"
                  >
                    <div className="hover-text">
                      <span>Vehicle Log Types</span>
                    </div>
                    <div className="hover-text">
                      <FaArrowRight />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="admin-right-container">
            {pageStatus === "HQList" && <HeadQuarters />}
            {pageStatus === "CompaniesList" && <CompaniesNew />}
            {pageStatus === "HQCompanies" && <HQCompanyNew />}
            {pageStatus === "DriverCredentialTypes" && (
              <DriverCredentialTypeNew />
            )}
            {pageStatus === "DriverInfractionTypes" && (
              <DriverInfractionTypesNew />
            )}
            {pageStatus === "LocationTypes" && <LocationTypesNew />}
            {pageStatus === "ScheduleNoteTypes" && <ScheduleNoteTypesNew />}
            {pageStatus === "ScheduleStatusesTypes" && (
              <ScheduleStatusesTypesNew />
            )}
            {pageStatus === "ScheduleTransportationTypes" && (
              <ScheduleTransportationTypesNew />
            )}
            {pageStatus === "ScheduleTypes" && <ScheduleTypesNew />}
            {pageStatus === "VehicleLogTypes" && <VehicleLogTypesNew />}
            {pageStatus === "VehicleStatusesTypes" && (
              <VehicleStatusesTypesNew />
            )}
            {pageStatus === "ClientList" && <ClientListNew />}
            {pageStatus === "DriverList" && <DriverListNew />}
            {pageStatus === "DriverCredentials" && <DriverCredentialsNew />}
            {pageStatus === "DriverInfractions" && <DriverInfractionsNew />}
            {pageStatus === "DriverMedical" && <DriverMedicalNew />}
            {pageStatus === "DriverNotes" && <DriverNewNote />}
            {pageStatus === "LocationList" && <LocationNew />}
            {pageStatus === "AirportList" && <AirportNew />}
            {pageStatus === "AirlineList" && <AirlineNew />}
            {pageStatus === "HotelList" && <HotelNew />}
            {pageStatus === "RatesList" && <RatesNew />}
            {pageStatus === "ScheduleList" && <ScheduleListNew />}
            {pageStatus === "ScheduleNotes" && <ScheduleNotesNew />}
            {pageStatus === "VehicleLists" && <VehicleListNew />}
            {pageStatus === "VehicleLogs" && <VehicleLogsNew />}
            {pageStatus === "ScheduleImport" && (
              <ScheduleContent title="Schedule Import" />
            )}
          </div>
          {/* <div className="flex justify-between mt-4 save-button">
                        <div onClick={handleSave} className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button">
                            <span>Edit</span>
                        </div>
                        <div>
                            <div className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button">
                                <span>Cancel</span>
                            </div>
                        </div>
                    </div> */}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDetails;
